/**
 * Plain text, Markdown, source code and configuration files.
 *
 * The design goal is a byte-identical round trip when nothing is edited. That
 * sounds obvious and is routinely got wrong: viewers normalise CRLF to LF, drop
 * a trailing newline, or re-encode a legacy file — and the user's next commit
 * shows every line as changed. §13 forbids silent alteration, so the encoding,
 * the line-ending style, the BOM and the final-newline state are all carried in
 * the model and restored on save.
 *
 * The one deliberate change is §22's requirement to save as UTF-8. A EUC-KR
 * file does not round-trip byte-for-byte, and the handler says so through
 * `lossOnSave` rather than doing it quietly.
 */
import {
  FORMATS_BY_ID, decodeTextBytes, encodeTextForSave,
} from '@moduviewer/core';
import type { Encoding, LineEnding } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';

export interface TextModel {
  readonly text: string;
  /** Encoding the file arrived in. Saving always produces UTF-8 (§22). */
  readonly sourceEncoding: Encoding;
  readonly lineEnding: LineEnding;
  readonly hadBom: boolean;
  readonly lineCount: number;
}

/** Lines shown before the rest of the file is parsed (§11). */
const PREVIEW_LINES = 200;
/** Bytes read for the preview — enough to detect the encoding reliably. */
const PREVIEW_BYTES = 64 * 1024;

const TEXT_FORMAT_IDS = [
  'txt', 'markdown', 'sourcecode', 'dotenv', 'json', 'xml', 'yaml', 'sql',
] as const;

function countLines(text: string): number {
  if (text.length === 0) return 0;
  let n = 1;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c === 10) n++;
    else if (c === 13 && text.charCodeAt(i + 1) !== 10) n++;
  }
  return n;
}

function encodingWarnings(model: TextModel, formatId: string): HandlerWarning[] {
  const warnings: HandlerWarning[] = [];
  if (model.sourceEncoding !== 'utf-8') {
    warnings.push({
      messageKey: 'error.formattingMayNotBePreserved',
      severity: 'caution',
      detail: `saved as UTF-8; the original was ${model.sourceEncoding} (§22)`,
    });
  }
  if (model.lineEnding === 'mixed') {
    warnings.push({
      messageKey: 'status.limitedSupport',
      severity: 'info',
      detail: 'the file mixes line-ending styles; they are left exactly as they are',
    });
  }
  if (formatId === 'dotenv') {
    warnings.push({
      messageKey: 'detect.warning.sensitiveContent',
      severity: 'caution',
      detail: 'values are masked until explicitly revealed (§22, §29)',
    });
  }
  return warnings;
}

export const textHandler: FormatHandler<TextModel> = {
  id: 'text',
  formatIds: TEXT_FORMAT_IDS,

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const head = source.bytes.subarray(0, Math.min(source.bytes.length, PREVIEW_BYTES));
    const decoded = decodeTextBytes(head);

    // Drop the trailing fragment: the byte window almost never lands on a line
    // boundary, and showing half a line as if it were the file's content would
    // be a small lie in the very first thing the user sees.
    const complete = source.bytes.length > head.length;
    const lines = decoded.text.split(/\r\n|\r|\n/);
    if (complete && lines.length > 1) lines.pop();

    return {
      kind: 'text',
      unitCount: complete ? undefined : countLines(decoded.text),
      unitKey: undefined,
      firstContent: lines.slice(0, PREVIEW_LINES),
      warnings: decoded.detection.confidence === 'low'
        ? [{
          messageKey: 'status.limitedSupport',
          severity: 'caution',
          detail: `encoding could not be determined confidently; assumed ${decoded.detection.encoding}`,
        }]
        : [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<TextModel>> {
    throwIfAborted(ctx.signal);
    const decoded = decodeTextBytes(source.bytes);
    const model: TextModel = {
      text: decoded.text,
      sourceEncoding: decoded.detection.encoding,
      lineEnding: decoded.lineEnding,
      hadBom: decoded.detection.hasBom,
      lineCount: countLines(decoded.text),
    };

    const spec = FORMATS_BY_ID.get(source.formatId);
    // Past the editable-size budget the file still opens — it just opens
    // read-only, because holding the whole string plus an undo stack is what
    // actually exhausts memory (§33).
    const tooLargeToEdit = source.sizeBytes > ctx.limits.maxEditableTextBytes;

    const warnings = encodingWarnings(model, source.formatId);
    if (tooLargeToEdit) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'degraded',
        detail: `${source.sizeBytes} bytes exceeds the editable limit of ${ctx.limits.maxEditableTextBytes}`,
      });
    }

    return {
      formatId: source.formatId,
      model,
      readOnly: ctx.assessment.forceReadOnly || tooLargeToEdit,
      editLevel: spec?.edit ?? 'read-only',
      lossOnSave: model.sourceEncoding === 'utf-8' ? [] : [
        `저장 시 UTF-8로 변환됩니다 (원본 인코딩: ${model.sourceEncoding})`,
      ],
      warnings,
    };
  },

  async exportBytes(
    model: TextModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const bytes = encodeTextForSave(model.text, model.lineEnding, {
      // A BOM the file arrived with is kept: some Windows tools depend on it,
      // and removing it would change how other software reads the file.
      withBom: model.hadBom && model.sourceEncoding === 'utf-8',
    });
    return {
      bytes,
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped: model.sourceEncoding === 'utf-8' ? [] : [`original ${model.sourceEncoding} encoding`],
    };
  },

  async validateExport(
    bytes: Uint8Array, model: TextModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const decoded = decodeTextBytes(bytes);
      reopened = true;
      contentPreserved = decoded.text === model.text;
      if (!contentPreserved) issues.push('reopened text differs from the exported model');
      if (decoded.lineEnding !== model.lineEnding) {
        issues.push(`line endings changed: ${model.lineEnding} → ${decoded.lineEnding}`);
      }
    } catch (err) {
      issues.push(`could not reopen the exported file: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved && issues.length === 0, reopened, contentPreserved, issues };
  },
};

/** Replace the text, keeping the encoding and formatting metadata. */
export function withText(model: TextModel, text: string): TextModel {
  return { ...model, text, lineCount: countLines(text) };
}

/** Guard used by the editor before accepting an edit on a read-only document. */
export function assertEditable(doc: OpenedDocument<TextModel>): void {
  if (doc.readOnly) {
    throw new HandlerError('error.featuresUnsupported', 'this document is open read-only');
  }
}
